import React, { useEffect, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import { useQuery, useMutation } from 'hooks/apollo';
import { Autocomplete, Button, TextField } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { financialProductRuleCommissionModalVar } from './variables';
import { over, get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { Prompt } from 'react-router-dom';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import {
  FINANCIAL_PRODUCT_RULE_COMMISSION_LIST_QUERY,
  FINANCIAL_PRODUCT_RULE_COMMISSION_ASSIGNED_TO_LIST_QUERY,
  FINANCIAL_PRODUCT_RULE_COMMISSION_APPLIED_TO_LIST_QUERY,
  FINANCIAL_PRODUCT_RULE_COMMISSION_CALCULATION_METHOD_LIST_QUERY
} from '../../../queries';
import { financialProductRuleCommissionValidator } from '../../../validators';
import { getIntOrDefaultValue } from 'utils/functions';
import { ADD_UPDATE_FINANCIAL_PRODUCT_RULE_COMMISSION } from '../../../mutations';
import { useRouteMatch } from 'react-router-dom';
import { getInt } from 'utils/functions';

const FinancialProductRulesEditDetailsCommissionsEditComponent = ({ commission }) => {
  const { t } = useTranslation();

  const [commissionLens, dirtyInfo] = useChangeTrackingLens(commission);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const match = useRouteMatch();
  const ruleId = match.params.ruleId && getInt(match.params.ruleId);
  const [validation, validate] = useDirtyFieldValidation(financialProductRuleCommissionValidator);

  useEffect(() => {
    validate(commissionLens |> get, dirtyInfo);
  }, [dirtyInfo, commissionLens, validate]);

  const [saveFinancialProductRuleCommission] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_RULE_COMMISSION, {
    onCompleted: _ => setSaving(false),
    onError: () => {
      setSaving(false);
    }
  });

  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(() => {
    const comm = get(commissionLens);
    if (!validate(comm)) return;
    setSaving(true);
    saveFinancialProductRuleCommission({
      variables: {
        input: {
          financialProductRuleCommissionId: parseInt(comm.financialProductRuleCommissionId),
          financialProductRuleId: ruleId,
          readOnlyYN: comm.readOnlyYN,
          commissionId: comm.commissionId,
          commissionDefaultPercent: comm.usesPercent ? parseFloat(comm.commissionDefaultPercent) : null,
          commissionDefaultValue: comm.usesValue ? parseFloat(comm.commissionDefaultValue) : null,
          assignToRoleId: comm.assignToRoleId,
          appliedToId: comm.appliedToId,
          calculationMethodId: comm.calculationMethodId,
          implicit: comm.implicit,
          mandatory: comm.mandatory
        }
      }
    });
  }, [commissionLens, saveFinancialProductRuleCommission, validate, ruleId]);

  const { data: financialProductRuleCommissionList } = useQuery(FINANCIAL_PRODUCT_RULE_COMMISSION_LIST_QUERY);

  const { data: financialProductRuleCommissionAssignedToList } = useQuery(FINANCIAL_PRODUCT_RULE_COMMISSION_ASSIGNED_TO_LIST_QUERY);

  const { data: financialProductRuleCommissionAppliedToList } = useQuery(FINANCIAL_PRODUCT_RULE_COMMISSION_APPLIED_TO_LIST_QUERY);

  const { data: financialProductRuleCommissionCalculationMethodList } = useQuery(
    FINANCIAL_PRODUCT_RULE_COMMISSION_CALCULATION_METHOD_LIST_QUERY
  );

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('FinancialProduct.Details.Rules.Details.Commissions.Name')}
            required
            valueKey={'commissionId'}
            labelKey={'commissionName'}
            value={{ commissionId: commissionLens.commissionId |> get, commissionName: commissionLens.commissionName |> get }}
            options={financialProductRuleCommissionList?.financialProductRuleCommissionList}
            onChange={event => {
              over(commissionLens, oldState => ({
                ...oldState,
                commissionId: event.commissionId,
                commissionName: event.commissionName,
                calcTypeId: event.calcTypeId,
                calcTypeName: event.calcTypeName
              }));
            }}
            error={!isValid(validation.commissionId)}
            helperText={getErrors(validation.commissionId)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            disabled={true}
            fullWidth
            label={t('FinancialProduct.Details.Rules.Details.Commissions.CalculatedAt')}
            value={get(commissionLens.calcTypeName) || ''}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            isNumeric
            fullWidth
            variant='standard'
            disabled={!(commissionLens.usesPercent |> get)}
            label={t('FinancialProduct.Details.Rules.Details.Commissions.Percent')}
            value={get(commissionLens.commissionDefaultPercent)}
            onChange={event => {
              over(commissionLens, oldState => ({
                ...oldState,
                commissionDefaultPercent: event,
                usesValue: getIntOrDefaultValue(event, -9999) === 0,
                usesPercent: true
              }));
            }}
            error={!isValid(validation.commissionDefaultPercent)}
            helperText={getErrors(validation.commissionDefaultPercent)}
            inputProps={{
              maxLength: 25
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            isNumeric
            fullWidth
            variant='standard'
            disabled={!(commissionLens.usesValue |> get)}
            label={t('FinancialProduct.Details.Rules.Details.Commissions.Value')}
            value={get(commissionLens.commissionDefaultValue)}
            onChange={event => {
              over(commissionLens, oldState => ({
                ...oldState,
                commissionDefaultValue: event,
                usesValue: true,
                usesPercent: getIntOrDefaultValue(event, -9999) === 0
              }));
            }}
            error={!isValid(validation.commissionDefaultValue)}
            helperText={getErrors(validation.commissionDefaultValue)}
            inputProps={{
              maxLength: 25
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('FinancialProduct.Details.Rules.Details.Commissions.AppliedTo')}
            required
            valueKey={'appliedToId'}
            labelKey={'appliedToName'}
            value={{ appliedToId: commissionLens.appliedToId |> get, appliedToName: commissionLens.appliedToName |> get }}
            options={financialProductRuleCommissionAppliedToList?.financialProductRuleCommissionAppliedToList}
            onChange={event => {
              over(commissionLens, oldState => ({
                ...oldState,
                appliedToId: event.appliedToId,
                appliedToName: event.appliedToName
              }));
            }}
            error={!isValid(validation.appliedToId)}
            helperText={getErrors(validation.appliedToId)}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('FinancialProduct.Details.Rules.Details.Commissions.AssignedTo')}
            required
            valueKey={'assignToRoleId'}
            labelKey={'assignToRoleName'}
            value={{ assignToRoleId: commissionLens.assignToRoleId |> get, assignToRoleName: commissionLens.assignToRoleName |> get }}
            options={financialProductRuleCommissionAssignedToList?.financialProductRuleCommissionAssignedToList}
            onChange={event => {
              over(commissionLens, oldState => ({
                ...oldState,
                assignToRoleId: event.assignToRoleId,
                assignToRoleName: event.assignToRoleName
              }));
            }}
            error={!isValid(validation.assignToRoleId)}
            helperText={getErrors(validation.assignToRoleId)}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            fullWidth
            label={t('FinancialProduct.Details.Rules.Details.Commissions.CalculationMethod')}
            required
            valueKey={'calculationMethodId'}
            labelKey={'calculationMethodName'}
            value={{
              calculationMethodId: commissionLens.calculationMethodId |> get,
              calculationMethodName: commissionLens.calculationMethodName |> get
            }}
            options={financialProductRuleCommissionCalculationMethodList?.financialProductRuleCommissionCalculationMethodList}
            onChange={event => {
              over(commissionLens, oldState => ({
                ...oldState,
                calculationMethodId: event.calculationMethodId,
                calculationMethodName: event.calculationMethodName
              }));
            }}
            error={!isValid(validation.calculationMethodId)}
            helperText={getErrors(validation.calculationMethodId)}
          />
        </Grid>
        <Grid item xs={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={commissionLens.implicit |> get}
                onChange={() => set(commissionLens.implicit)(!(commissionLens.implicit |> get))}
              />
            }
            label={t('FinancialProduct.Details.Rules.Details.Commissions.Default')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={commissionLens.mandatory |> get}
                onChange={() => set(commissionLens.mandatory)(!(commissionLens.mandatory |> get))}
              />
            }
            label={t('FinancialProduct.Details.Rules.Details.Commissions.Mandatory')}
            labelPlacement='end'
          />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={commissionLens.readOnlyYN |> get}
                onChange={() => set(commissionLens.readOnlyYN)(!(commissionLens.readOnlyYN |> get))}
              />
            }
            label={t('FinancialProduct.Details.Rules.Details.Commissions.ReadOnly')}
            labelPlacement='end'
          />
        </Grid>
        <Grid container item xs={12} justifyContent='flex-end'>
          <Button onClick={handleSave} sx={{ mr: 1 }} disabled={saving}>
            {saving ? t('Saving') : t('Save')}
          </Button>
          <Button onClick={() => financialProductRuleCommissionModalVar({ open: false })}>{t('Close')}</Button>
        </Grid>
      </Grid>
    </>
  );
};

FinancialProductRulesEditDetailsCommissionsEditComponent.propTypes = {
  commission: PropTypes.object.isRequired
};

export default FinancialProductRulesEditDetailsCommissionsEditComponent;
